/**
 * Lowercases, then collapses every run of non-alphanumeric characters to a single space, and trims.
 *
 * This is what makes "Sherwin Williams" find a product whose manufacturer is stored as
 * "Sherwin-Williams" (ENG-967) — people do not type the hyphen. It also absorbs a trailing space from
 * a search box, and makes "S-W" match a label that starts "S-W ". It is not aliasing: "S-W" still
 * finds nothing in "Sherwin-Williams", because "s w" is not a substring of "sherwin williams".
 */
export function normalizeForSearch(text: string): string {
  return (text ?? '')
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * True when any of `texts` contains `search` after both sides are normalized.
 *
 * `search` is normalized here too, so callers can pass raw search-box text. An empty search matches
 * anything that has text, which keeps "no query" and "query of only punctuation" behaving the same.
 * An item with no searchable text never matches, empty search included — a dropdown option with no
 * label and no sublabel renders blank, so it stays out of the list rather than showing as a gap.
 */
export function matchesSearchText(args: { texts: Array<string | undefined | null>; search?: string }): boolean {
  const { texts, search } = args
  const searchableTexts = texts.filter(Boolean)

  if (!searchableTexts.length) {
    return false
  }

  const normalizedSearch = normalizeForSearch(search ?? '')

  if (!normalizedSearch) {
    return true
  }

  return searchableTexts.some((text) => normalizeForSearch(text).includes(normalizedSearch))
}
